import { OutlinedSelect, SelectOptionType, Button } from '@octano/global-ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useCallback } from 'react';

import { TranslationsKeys } from '../../../../locales/translations';
import { TutorAssignmentFilters, TutorAssignmentForm } from '../types';

type FormValues = Record<keyof TutorAssignmentFilters, SelectOptionType | null>;

const DEFAULT_FORM_VALUES: FormValues = {
  school: null,
  studyPlan: null,
  period: null,
  campus: null,
  schedule: null,
  modality: null,
};

const dictPrefix = 'searchControls';

type SearchControlsProps = Partial<TutorAssignmentForm> & {
  loading?: boolean;
  onDownloadBase?: (filters: TutorAssignmentFilters) => void;
  onDownloadTemplate?: (filters: TutorAssignmentFilters) => void;
};

const SearchControls = ({
  loading = false,
  schools = [],
  studyPlans = [],
  periods = [],
  campuses = [],
  schedules = [],
  modalities = [],
  onDownloadBase,
  onDownloadTemplate,
}: SearchControlsProps) => {
  const { t } = useTranslation(
    TranslationsKeys.TUTOR_MANAGEMENT_TUTOR_ASSIGNMENT,
  );

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const handleClear = useCallback(() => {
    if (loading) {
      return;
    }
    reset(DEFAULT_FORM_VALUES);
  }, [loading, reset]);

  const valuesToFilters = useCallback(
    (values: FormValues): TutorAssignmentFilters => {
      const filters: TutorAssignmentFilters = {};
      Object.entries(values).forEach((row) => {
        const key = row[0] as keyof TutorAssignmentFilters;
        const val = row[1];
        if (val) {
          filters[key] = val?.value;
        }
      });
      return filters;
    },
    [],
  );

  const handelDownloadBase = useCallback(
    (values: FormValues) => {
      if (loading || !onDownloadBase) {
        return;
      }
      onDownloadBase(valuesToFilters(values));
    },
    [loading, onDownloadBase, valuesToFilters],
  );
  const handelDownloadTemplate = useCallback(
    (values: FormValues) => {
      if (loading || !onDownloadTemplate) {
        return;
      }
      onDownloadTemplate(valuesToFilters(values));
    },
    [loading, onDownloadTemplate, valuesToFilters],
  );

  return (
    <Row className="mb-5">
      <Col xs={12} sm={6} md={6} lg={4} xl={3}>
        <OutlinedSelect
          control={control}
          name="school"
          label={t(`${dictPrefix}.school.label`)}
          placeholder={t(`${dictPrefix}.school.placeholder`)}
          options={schools ?? []}
          disabled={!schools?.length || loading}
        />
      </Col>
      <Col xs={12} sm={6} md={6} lg={4} xl={3}>
        <OutlinedSelect
          control={control}
          name="studyPlan"
          label={t(`${dictPrefix}.studyPlan.label`)}
          placeholder={t(`${dictPrefix}.studyPlan.placeholder`)}
          options={studyPlans ?? []}
          disabled={!studyPlans?.length || loading}
        />
      </Col>
      <Col xs={12} sm={6} md={6} lg={4} xl={3}>
        <OutlinedSelect
          control={control}
          name="period"
          label={t(`${dictPrefix}.period.label`)}
          placeholder={t(`${dictPrefix}.period.placeholder`)}
          options={periods ?? []}
          disabled={!periods?.length || loading}
        />
      </Col>
      <Col xs={12} sm={6} md={6} lg={4} xl={3}>
        <OutlinedSelect
          control={control}
          name="campus"
          label={t(`${dictPrefix}.campus.label`)}
          placeholder={t(`${dictPrefix}.campus.placeholder`)}
          options={campuses ?? []}
          disabled={!campuses?.length || loading}
        />
      </Col>
      <Col xs={12} sm={6} md={6} lg={4} xl={3}>
        <OutlinedSelect
          control={control}
          name="schedule"
          label={t(`${dictPrefix}.schedule.label`)}
          placeholder={t(`${dictPrefix}.schedule.placeholder`)}
          options={schedules ?? []}
          disabled={!schedules?.length || loading}
        />
      </Col>
      <Col xs={12} sm={6} md={6} lg={4} xl={3}>
        <OutlinedSelect
          control={control}
          name="modality"
          label={t(`${dictPrefix}.modality.label`)}
          placeholder={t(`${dictPrefix}.modality.placeholder`)}
          options={modalities ?? []}
          disabled={!modalities?.length || loading}
        />
      </Col>
      <Col xs={12} xl={6}>
        <div className="d-flex flex-wrap w-100 align-items-center justify-content-end h-100">
          <div className="flex-fill px-2" />
          <div>
            <div className="d-flex w-100 align-items-center" style={{ gap: 8 }}>
              <div className="flex-fill">
                <Button
                  size="md"
                  text={t(`${dictPrefix}.actions.clear`)}
                  className="w-100"
                  loading={loading}
                  onClick={handleClear}
                  outlined
                />
              </div>
            </div>
          </div>
        </div>
      </Col>
      <Col xs={12}>
        <div className="d-flex flex-wrap w-100 align-items-center justify-content-end mt-4">
          <div>
            <div
              className="d-flex w-100 flex-wrap align-items-center justify-content-end"
              style={{ gap: 8 }}
            >
              <Button
                size="md"
                text={t(`${dictPrefix}.actions.downloadBase`)}
                icon="download"
                loading={loading}
                onClick={handleSubmit(handelDownloadBase)}
              />
              <Button
                size="md"
                text={t(`${dictPrefix}.actions.downloadTemplate`)}
                icon="download"
                loading={loading}
                onClick={handleSubmit(handelDownloadTemplate)}
              />
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default SearchControls;
